import React, { useMemo } from "react";
import { MapContainer, TileLayer, GeoJSON, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import regions_italy from "../../../utilities/geojson/regions_italy.json";
import { useAppContext } from "../../../AppProvider";
import { useShipmentsStatisticsContext } from "../providers/ShipmentsStatistics.tsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLoader } from "../../../utilities/fontawesomeIcons";
import { isEmpty } from "lodash";
import moment from "moment";
import { useInView } from "react-intersection-observer";

const calculateColorRanges = (values, numberOfBuckets = 6) => {
  const min = Math.min(...values);
  const max = Math.max(...values);
  const step = Math.ceil((max - min + 1) / numberOfBuckets);
  const ranges = [];
  for (let i = 0; i < numberOfBuckets; i++) {
    ranges.push(min + i * step);
  }
  return ranges;
};

const getColor = (value, ranges) => {
  const colors = [
    "#FFEDA0",
    "#FEB24C",
    "#FD8D3C",
    "#FC4E2A",
    "#E31A1C",
    "#BD0026",
    "#800026",
  ];
  for (let i = ranges.length - 1; i >= 0; i--) {
    if (value >= ranges[i]) {
      return colors[i];
    }
  }
  return colors[0];
};

const style = (feature, ranges) => ({
  fillColor: getColor(feature.properties.total || 0, ranges),
  weight: 2,
  opacity: 1,
  color: "white",
  dashArray: "3",
  fillOpacity: 0.7,
});

const createOnEachFeature = (dictionary) => (feature, layer) => {
  const name = feature.properties.name;
  const total = feature.properties.total || 0;
  layer.bindPopup(
    `<strong>${name}</strong><br/>${dictionary.shipments.shipments}: ${total}`
  );
};

const Legend = React.memo(({ ranges }) => {
  const { dictionary } = useAppContext();
  const map = useMap();

  React.useEffect(() => {
    const legend = L.control({ position: "bottomright" });

    legend.onAdd = function () {
      const div = L.DomUtil.create("div", "info legend");
      div.style.backgroundColor = "#fff";
      div.style.borderRadius = "5px";
      div.style.padding = "10px";
      const labels = [];

      for (let i = 0; i < ranges.length; i++) {
        const from = ranges[i];
        const to = ranges[i + 1];
        labels.push(
          `<span style="display:inline-block; width:10px; height:10px; background:${getColor(
            from + 1,
            ranges
          )}"></span> <label class="label-2 d-inline">${from}${
            to ? "&ndash;" + to : "+"
          }</label>`
        );
      }

      div.innerHTML = `<label class="label-3 mb-2">${
        dictionary.words.legend
      }</label> ${labels.join("<br>")}`;
      return div;
    };

    legend.addTo(map);
    return () => {
      legend.remove();
    };
  }, [map, ranges]);

  return null;
});

const MemoizedMap = React.memo(({ data, ranges, dictionary }) => (
  <MapContainer
    style={{ height: "500px", width: "100%" }}
    center={[41.8719, 12.5674]}
    zoom={5}
    scrollWheelZoom={false}
  >
    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
    <GeoJSON
      data={data}
      style={(feature) => style(feature, ranges)}
      onEachFeature={createOnEachFeature(dictionary)}
    />
    <Legend ranges={ranges} />
  </MapContainer>
));

const ChoroplethMapOfItalianRegions = () => {
  const { data, loader, filters } = useShipmentsStatisticsContext();
  const { dictionary } = useAppContext();
  const { ref, inView } = useInView({ triggerOnce: true });

  const { italianRegions, ranges } = useMemo(() => {
    if (isEmpty(data)) return { italianRegions: null, ranges: [] };

    const totals = data.regions?.map((r) => r.total || 0) || [];
    const newRanges = calculateColorRanges(totals);

    const updatedRegions = {
      ...regions_italy,
      features: regions_italy.features.map((f) => {
        const region = data.regions?.find(
          (r) => r.type === f.properties["name"]
        );
        return {
          ...f,
          properties: {
            ...f.properties,
            total: region ? region.total : 0,
          },
        };
      }),
    };

    return { italianRegions: updatedRegions, ranges: newRanges };
  }, [data]);

  return (
    <div ref={ref} className="box-container text-center h-100">
      <div className="row mb-1" style={{ height: "60px" }}>
        <div className="col">
          <label className="label-4 lh-1 mb-1">
            {dictionary.kpi.deliveries_distribution}
          </label>
          <label className="label-2">
            {moment(filters.start).isSame(moment(filters.end), "day")
              ? moment(filters.start).format("DD/MM/YYYY")
              : moment(filters.start).format("DD/MM/YYYY") +
                " - " +
                moment(filters.end).format("DD/MM/YYYY")}
          </label>
        </div>
      </div>

      <div className="row mt-2">
        <div className="col">
          {loader ? (
            <FontAwesomeIcon icon={faLoader} spin size="4x" />
          ) : data?.regions?.length > 0 && italianRegions ? (
            inView && (
              <MemoizedMap
                data={italianRegions}
                ranges={ranges}
                dictionary={dictionary}
              />
            )
          ) : (
            <label className="label-3">
              {dictionary.messages.no_results_found}
            </label>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChoroplethMapOfItalianRegions;
